/* eslint-disable react/prop-types */
import {Component} from 'react';

import {lazyInject} from '../../../context/module';
import {LoginService} from '../../../services/login.service';
import {AccountDetails} from '../../../utils/request.utils';

import styles from './portal-account-menu.component.module.scss';

interface State {
    open: boolean;
    userDetails: AccountDetails | null;
    signingOut: boolean;
}

/**
 * Stripe-style account menu mounted in the top-right corner of
 * `<PortalLayout>`.
 *
 * Behaviour:
 * - Closes on outside click + ESC.
 * - Avatar shows the first two letters of the user's email.
 * - "Sign out" calls `LoginService.logout()` and redirects to `/`.
 * - "My Account" links to `/portal`.
 *
 * Class component + `lazyInject` to match the rest of the portal.
 */
export default class PortalAccountMenu extends Component<{}, State> {
    @lazyInject(LoginService) private loginService!: LoginService;

    private rootRef: HTMLDivElement | null = null;
    private userDetailsSub: {unsubscribe: () => void} | null = null;

    state: State = {
        open: false,
        userDetails: null,
        signingOut: false,
    };

    componentDidMount() {
        // Seed from any already-cached value, then subscribe for updates.
        if (this.loginService.userDetails) {
            this.setState({userDetails: this.loginService.userDetails});
        }
        const sub = this.loginService.onUserDetailsChanged.subscribe({
            next: (details) => this.setState({userDetails: details}),
            error: () => this.setState({userDetails: null}),
        });
        // RxJS Subscription has .unsubscribe(); store loosely-typed handle.
        this.userDetailsSub = sub as unknown as {unsubscribe: () => void};

        if (typeof document !== 'undefined') {
            document.addEventListener('mousedown', this.handleDocumentClick);
            document.addEventListener('keydown', this.handleKeyDown);
        }
    }

    componentWillUnmount() {
        if (this.userDetailsSub) {
            try {
                this.userDetailsSub.unsubscribe();
            } catch {
                /* noop */
            }
            this.userDetailsSub = null;
        }
        if (typeof document !== 'undefined') {
            document.removeEventListener('mousedown', this.handleDocumentClick);
            document.removeEventListener('keydown', this.handleKeyDown);
        }
    }

    private setRootRef = (el: HTMLDivElement | null) => {
        this.rootRef = el;
    };

    private toggle = () => {
        this.setState((s) => ({open: !s.open}));
    };

    private close = () => {
        if (this.state.open) {
            this.setState({open: false});
        }
    };

    private handleDocumentClick = (e: MouseEvent) => {
        if (!this.state.open) return;
        const target = e.target as Node | null;
        if (this.rootRef && target && !this.rootRef.contains(target)) {
            this.close();
        }
    };

    private handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && this.state.open) {
            this.close();
        }
    };

    private handleSignOut = () => {
        if (this.state.signingOut) return;
        this.setState({signingOut: true, open: false});
        this.loginService.logout().subscribe({
            next: () => {
                // Land on the login screen (/portal shows the "Welcome back" form when
                // logged out) rather than the homepage — clearer post-logout UX.
                if (typeof window !== 'undefined') {
                    window.location.href = '/portal';
                }
            },
            error: () => {
                // Even if the server call fails, send the user to the login screen.
                if (typeof window !== 'undefined') {
                    window.location.href = '/portal';
                }
            },
        });
    };

    private initials(): string {
        const email = this.state.userDetails?.email;
        if (!email) return 'NA';
        const cleaned = email.trim();
        if (cleaned.length === 0) return 'NA';
        return cleaned.slice(0, 2).toUpperCase();
    }

    render() {
        const {open, userDetails, signingOut} = this.state;
        // Hide the menu entirely when there is no authenticated session;
        // the logged-out portal landing page renders its own sign-in card.
        if (!userDetails || !userDetails.email) {
            return null;
        }
        const email = userDetails.email;
        const triggerCls = open
            ? `${styles.trigger} ${styles.triggerOpen}`
            : styles.trigger;

        return (
            <div className={styles.root} ref={this.setRootRef}>
                <button
                    type="button"
                    className={triggerCls}
                    onClick={this.toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Open account menu"
                    data-testid="portal-account-menu-trigger"
                >
                    <span className={styles.avatar} aria-hidden="true">
                        {this.initials()}
                    </span>
                    <span className={styles.email}>{email}</span>
                    <span className={styles.chevron} aria-hidden="true">
                        <svg
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M3 4.5L6 7.5L9 4.5"
                                stroke="currentColor"
                                strokeWidth="1.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </span>
                </button>

                {open && (
                    <div
                        className={styles.menu}
                        role="menu"
                        data-testid="portal-account-menu-dropdown"
                    >
                        <div className={styles.menuHeader}>
                            <span className={styles.menuHeaderLabel}>
                                Signed in as
                            </span>
                            <span className={styles.menuHeaderValue}>{email}</span>
                        </div>
                        <a
                            href="/portal"
                            role="menuitem"
                            className={styles.menuItem}
                            data-testid="portal-account-menu-my-account"
                            onClick={this.close}
                        >
                            <span className={styles.menuItemIcon} aria-hidden="true">
                                <svg
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M8 8C9.65685 8 11 6.65685 11 5C11 3.34315 9.65685 2 8 2C6.34315 2 5 3.34315 5 5C5 6.65685 6.34315 8 8 8Z"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                    />
                                    <path
                                        d="M2.5 14C2.5 11.2386 4.96243 9 8 9C11.0376 9 13.5 11.2386 13.5 14"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </span>
                            My Account
                        </a>
                        <a
                            href="/portal/billing"
                            role="menuitem"
                            className={styles.menuItem}
                            onClick={this.close}
                        >
                            <span className={styles.menuItemIcon} aria-hidden="true">
                                <svg
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <rect
                                        x="2"
                                        y="4"
                                        width="12"
                                        height="9"
                                        rx="1.5"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                    />
                                    <path
                                        d="M2 7H14"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                    />
                                </svg>
                            </span>
                            Billing
                        </a>
                        <div className={styles.divider} aria-hidden="true" />
                        <button
                            type="button"
                            role="menuitem"
                            className={`${styles.menuItem} ${styles.menuItemDanger}`}
                            onClick={this.handleSignOut}
                            disabled={signingOut}
                            data-testid="portal-account-menu-sign-out"
                        >
                            <span className={styles.menuItemIcon} aria-hidden="true">
                                <svg
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M9.5 2H3.5C2.94772 2 2.5 2.44772 2.5 3V13C2.5 13.5523 2.94772 14 3.5 14H9.5"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        d="M11 5.5L13.5 8L11 10.5"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M13 8H6"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </span>
                            {signingOut ? 'Signing out…' : 'Sign out'}
                        </button>
                    </div>
                )}
            </div>
        );
    }
}
